import React, { useState } from "react";
import { createStyles, Theme, makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Divider from "@material-ui/core/Divider";
import { useTranslation } from "react-i18next";
import Loading from "customHooks/useCompleoReactHookForm/helpers/Loading";
import FormHelperText from "@material-ui/core/FormHelperText";
import { useAuthState } from "_ReactContext/AuthContext";
import {
    DragDropContext,
    Droppable,
    DroppableProvided,
    DropResult
} from "react-beautiful-dnd";
import StageActionsModal from "./StageActionsModal";
import useGetMetadata from "customHooks/useGetMetadata";
import StagesItems from "./StageItems";
import AddStageButton from "./AddStageButton";
import { ModalDataType } from "./helperJobApprovalWorkflow";
import { useGlobalDialog } from "_ReactContext/GlobalDialogContext";
import {
    IInputProps,
    useRhfFieldWithoutControllerOrRegister
} from "customHooks/useCompleoReactHookForm/helpers/reactHookFormsHelper";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        ol: {
            paddingLeft: theme.spacing(1)
        },
        root: {
            width: "100%",
            backgroundColor: theme.palette.background.paper,
            marginTop: theme.spacing(1)
        },
        nested: {
            paddingLeft: theme.spacing(6)
        },
        inline: {
            display: "inline"
        },
        listCheck: {
            minWidth: 36
        },
        colorError: {
            color: theme.palette.error.main
        },
        color: {}
    })
);

interface IProps {
    stageIdsWithApprovers?: string[];
}

const ApprovalStages = (props: IProps & IInputProps) => {
    const classes = useStyles();
    const { stageIdsWithApprovers = [] } = props;
    const fieldName = props.metadata.fieldName;
    const {
        error,
        message,
        setFieldValue,
        watch
    } = useRhfFieldWithoutControllerOrRegister(
        fieldName,
        props.helperTextDefault
    );

    const { callDialog } = useGlobalDialog();
    const [modalData, setModalData] = useState<ModalDataType>({
        open: false,
        stage: {},
        index: 0,
        name: fieldName,
        new: false
    });

    const modalModuleName = "JOBAPPROVALWORKFLOWSTAGEMAIN";
    const [t, i18n, readyTranslation] = useTranslation(modalModuleName, {
        useSuspense: false
    });

    const [modalMetadata] = useGetMetadata(modalModuleName);

    const stageData = watch(fieldName) || [];

    React.useEffect(() => {
        if (readyTranslation && (!stageData || stageData?.length === 0)) {
            // Initialize with an empty array if no stages exist
            setFieldValue([]);
        }
    }, [readyTranslation]);

    const handleModalClose = () => {
        setModalData({ ...modalData, open: false });
    };

    const handleEditStage = (stage: any, index: number) => {
        setModalData({
            ...modalData,
            stage: stage,
            open: true,
            index: index,
            new: false
        });
    };

    const handleAddStage = (index: number) => {
        const newStage = {
            name: "",
            expirationDays: 7, // Default expiration days
            approvalLogic: {
                value: "or"
            }, // Default approval logic
            order: index + 1
        };

        setModalData({
            ...modalData,
            stage: newStage,
            open: true,
            index: index,
            new: true
        });
    };

    const handleDeleteStage = (stage: any) => {
        if (stageIdsWithApprovers.includes(stage.id)) {
            callDialog({
                title: t("stageWithApproversDeleteTitle"),
                bodyText: t("stageWithApproversDelete"),
                agreeButtonText: t("COMPLEOGENERAL_CLOSE"),
                agreeFunction: async () => {
                    return false;
                }
            });
        } else {
            // Renumber remaining stages after removal, mirroring onDragEnd.
            // Order gaps (e.g. [0, 2]) break the approval flow progression.
            // See: https://github.com/compleoteam/compleo-v3/issues/1323
            const newValue = [...stageData]
                .filter((item: any) => item.id !== stage.id)
                .map((item: any, index: number) => ({
                    ...item,
                    order: index
                }));
            setFieldValue(newValue);
        }
    };

    const onDragEnd = (result: DropResult) => {
        const { destination, source, draggableId } = result;

        if (destination) {
            if (destination.index === source.index) {
                return;
            }
            const stagesDestination = [...stageData];
            const currentItem = stageData.filter(
                (item: any) => item.name === draggableId
            )[0];
            const mainListCurrentItem = stageData.findIndex(
                (item: any) => item.name === draggableId
            );
            const indexDifference = mainListCurrentItem - source.index;
            stagesDestination.splice(mainListCurrentItem, 1);
            stagesDestination.splice(
                destination.index + indexDifference,
                0,
                currentItem
            );
            const reorderedStages = stagesDestination.map(
                (item: any, index: any) => {
                    return {
                        ...item,
                        order: index
                    };
                }
            );
            setFieldValue(reorderedStages);
        }
    };

    let classError = classes.color;
    if (error) {
        classError = classes.colorError;
    }

    let listStages: any = stageData || [];

    if (readyTranslation) {
        return (
            <React.Fragment>
                {modalData.open && (
                    <StageActionsModal
                        modalData={modalData}
                        handleModalClose={handleModalClose}
                        t={t}
                        mainFieldName={fieldName}
                        setFieldValue={setFieldValue}
                        metadata={modalMetadata}
                        i18n={i18n}
                        readyTranslation={readyTranslation}
                        stageIdsWithApprovers={stageIdsWithApprovers}
                    />
                )}
                <List component="nav" className={classes.root}>
                    {error && (
                        <List>
                            <ListItem alignItems="flex-start">
                                <ListItemText
                                    primary={
                                        <FormHelperText
                                            id="component-helper-text"
                                            error
                                        >
                                            {message}
                                        </FormHelperText>
                                    }
                                />
                            </ListItem>
                        </List>
                    )}
                    <Divider />
                    <ListItem alignItems="flex-start">
                        <ListItemText className={classError} primary={""} />
                    </ListItem>
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable
                            droppableId="approvalStages"
                            direction="vertical"
                            type="approvalStages"
                        >
                            {(provided: DroppableProvided) => (
                                <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                >
                                    <StagesItems
                                        classError={classError}
                                        listStages={listStages}
                                        t={t}
                                        handleEditStage={handleEditStage}
                                        handleDeleteStage={handleDeleteStage}
                                        stageIdsWithApprovers={
                                            stageIdsWithApprovers
                                        }
                                    ></StagesItems>
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                    <AddStageButton
                        handleAddStage={handleAddStage}
                        listStages={listStages}
                        t={t}
                    />
                    <Divider />
                </List>
            </React.Fragment>
        );
    } else {
        return <Loading />;
    }
};

export default ApprovalStages;
